import { ClickOffer } from "@lib/shared/types";
import { PageProps } from "@services/initial-calls";
import { useRouter } from "next/router";
import {
    createContext,
    Dispatch,
    ReactElement,
    ReactNode,
    SetStateAction,
    useContext,
    useMemo,
    useState,
} from "react";
import { DomainForm } from "src/api";
import { useTimeOnSite } from "./useTimeOnSite";
import { updateDomainOffers } from "src/api/domainInfo";
import { getUserState } from "@lib/shared/cookies";

type DomainContextProps = Pick<
    PageProps,
    | "visitDetails"
    | "zipCodeLocation"
    | "domain"
    | "domainPopup"
    | "comparisonListing"
    | "formattedTitle"
    | "categoryListing"
    | "category"
    | "clicksOffers"
    | "zipCode"
    | "form"
    | "locale"
    | "defaultLocale"
    | "locales"
    | "content"
    | "userAgent"
    | "isBot"
    | "formTemplate"
    | "formOffer"
    | "exitModalOffer"
    | "thankYouOffers"
    | "headerOffer"
    | "thankyouListing"
    | "secondListing"
    | "stickyOffer"
    | "testConfig"
    | "blogPost"
    | "autoWarrantyMakers"
    | "totalTimeSpendOnSite"
    | "routeConfig"
    | "externalFavicon"
    | "pageBuilderBlogPost"
    | "pageBuilderLookupLists"
    | "pageBuilderListings"
    | "piiCompletionData"
    | "redirectTo"
    | "pageType"
    | "doNotSellFormStates"
    | "disableTimeOnSite"
    | "showFormModal"
    | "loadOneToOneForm"
    | "ignoreSticky"
> & {
    updateClicksOffers: Dispatch<
        SetStateAction<ClickOffer[] | null | undefined>
    >;
    updateForm: Dispatch<SetStateAction<DomainForm | null | undefined>>;
    selectedCountryCode: string | null;
    updateSelectedCountryCode: Dispatch<SetStateAction<string>>;
    handleUpdateOffers: (state: string) => void;
    loadingOffers: boolean;
};
// @ts-expect-error
const DomainContext = createContext<DomainContextProps>({});

export const DomainProvider = (
    props: PageProps & { children: ReactNode },
): ReactElement => {
    const {
        visitDetails,
        domain: initialDomain,
        formattedTitle,
        domainPopup,
        categoryListing,
        comparisonListing,
        zipCodeLocation,
        category,
        clicksOffers: offers,
        zipCode,
        form: domainForm,
        locale,
        locales,
        defaultLocale,
        content,
        userAgent,
        isBot,
        formTemplate,
        formOffer: initialFormOffer,
        headerOffer: initialHeaderOffer,
        stickyOffer: initialStickyOffer,
        exitModalOffer: initialExitModalOffer,
        thankYouOffers: initialThankYouOffers,
        thankyouListing,
        secondListing,
        testConfig,
        blogPost,
        initialTimeOnSite,
        routeConfig,
        externalFavicon,
        pageBuilderBlogPost,
        pageBuilderLookupLists,
        pageBuilderListings,
        autoWarrantyMakers,
        piiCompletionData,
        redirectTo,
        disableTimeOnSite,
        pageType,
        doNotSellFormStates,
        ignoreSticky,
    } = props;

    const [userSelectedCountryCode, updateSelectedCountryCode] = useState(
        visitDetails?.countryCode,
    );

    const { query } = useRouter();
    const selectedCountryCode = useMemo(() => {
        if (
            !domainForm?.supportedCountries ||
            !domainForm?.supportedCountries?.length ||
            userSelectedCountryCode === "US"
        ) {
            return null;
        }

        if (query?.countryCode) {
            return query?.countryCode as string;
        }

        return userSelectedCountryCode;
    }, [query?.countryCode, userSelectedCountryCode]);

    const [clicksOffers, updateClicksOffers] = useState(offers);
    const [form, updateForm] = useState(domainForm);
    const { totalTimeSpendOnSite } = useTimeOnSite(
        initialTimeOnSite ?? 0,
        disableTimeOnSite,
    );

    const [domainOffers, setDomainOffers] = useState({
        formOffer: initialFormOffer,
        headerOffer: initialHeaderOffer,
        stickyOffer: initialStickyOffer,
        exitModalOffer: initialExitModalOffer,
        thankYouOffers: initialThankYouOffers,
    });
    const [domain, setDomain] = useState(initialDomain);
    const [loadingOffers, setLoadingOffers] = useState(false);

    const loadOneToOneForm =
        (query.oto === "true" || category?.isOneToOneConsent) &&
        query.oto !== "false";

    const updateOffers = async (stateCode: string) => {
        setLoadingOffers(true);
        const { data } = await updateDomainOffers({
            categorySlug: category?.slug,
            stateCode: stateCode,
            isMobile: visitDetails.isMobile,
            query,
        });
        setLoadingOffers(false);

        if (data) {
            const {
                formOffer: updatedFormOffer,
                exitModalOffer: updatedExitModalOffer,
                thankYouOffers: updatedThankYouOffers,
                headerOffer: updatedHeaderOffer,
                stickyOffer: updatedStickyOffer,
                defaultOffers,
            } = data;

            setDomain({
                ...initialDomain,
                defaultOffers,
            });

            setDomainOffers({
                formOffer: updatedFormOffer ?? [],
                exitModalOffer: updatedExitModalOffer ?? [],
                thankYouOffers: updatedThankYouOffers ?? [],
                headerOffer: updatedHeaderOffer ?? [],
                stickyOffer: updatedStickyOffer ?? [],
            });
        }
    };

    const handleUpdateOffers = (state: string) => {
        const stateCookie = getUserState();
        if (
            state.toLowerCase() ===
            (stateCookie
                ? String(stateCookie)?.toLowerCase()
                : visitDetails.stateCode?.toLowerCase())
        ) {
            return;
        }
        void updateOffers(state);
    };

    return (
        <DomainContext.Provider
            value={{
                visitDetails,
                domain,
                formattedTitle,
                domainPopup,
                comparisonListing,
                categoryListing,
                zipCodeLocation,
                category,
                clicksOffers,
                zipCode,
                updateClicksOffers,
                form,
                updateForm,
                locale,
                defaultLocale,
                locales,
                content,
                userAgent,
                isBot,
                formTemplate,
                formOffer: domainOffers.formOffer,
                headerOffer: domainOffers.headerOffer,
                stickyOffer: domainOffers.stickyOffer,
                exitModalOffer: domainOffers.exitModalOffer,
                thankYouOffers: domainOffers.thankYouOffers,
                thankyouListing,
                secondListing,
                testConfig,
                blogPost,
                selectedCountryCode,
                updateSelectedCountryCode,
                totalTimeSpendOnSite: totalTimeSpendOnSite,
                routeConfig,
                externalFavicon,
                pageBuilderBlogPost,
                autoWarrantyMakers,
                pageBuilderLookupLists,
                pageBuilderListings,
                piiCompletionData,
                redirectTo,
                pageType,
                doNotSellFormStates,
                loadOneToOneForm,
                handleUpdateOffers,
                loadingOffers,
                ignoreSticky,
            }}
        >
            {props.children}
        </DomainContext.Provider>
    );
};

export const useDomainContext = () => useContext(DomainContext);
